import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getGoogleAuthUrl, GoogleOAuthError } from '../api/googleOAuthClient';

interface GoogleOAuthButtonProps {
    onError?: (error: Error) => void;
    disabled?: boolean;
    variant?: 'default' | 'icon-only';
    className?: string;
}

function GoogleIcon({ className = 'h-5 w-5' }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
            />
            <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
            />
            <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
            />
            <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
            />
        </svg>
    );
}

function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
    return (
        <svg
            className={`${className} animate-spin`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );
}

export function GoogleOAuthButton({
    onError,
    disabled,
    variant = 'default',
    className = ''
}: GoogleOAuthButtonProps) {
    const { t } = useTranslation();
    const authorization = useMutation({
        mutationFn: getGoogleAuthUrl,
        retry: false,
        onSuccess: ({ authUrl, state, codeVerifier }) => {
            sessionStorage.setItem('oauth_state', state);
            sessionStorage.setItem('oauth_code_verifier', codeVerifier);
            window.location.href = authUrl;
        },
        onError: (error) => {
            const err = error instanceof GoogleOAuthError
                ? new Error(t(error.translationKey))
                : error instanceof TypeError
                    ? new Error(t('account.oauth.networkError'))
                    : error instanceof Error ? error : new Error(t('account.errors.generic'));
            onError?.(err);
            console.error('[GoogleOAuth] Failed to initiate OAuth flow:', err.message);
        }
    });

    const baseClasses =
        'flex items-center justify-center gap-3 rounded-xl border border-ai-surface-glow/40 bg-white px-4 py-3 font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:cursor-not-allowed disabled:opacity-60';

    const variantClasses = variant === 'icon-only' ? 'w-12 px-0' : 'w-full text-sm sm:text-base';

    return (
        <button
            type="button"
            onClick={() => authorization.mutate()}
            disabled={disabled || authorization.isPending}
            className={`${baseClasses} ${variantClasses} ${className}`}
            aria-label={variant === 'icon-only' ? t('account.googleLogin') : undefined}
        >
            {authorization.isPending ? <Spinner /> : <GoogleIcon />}
            {variant === 'default' && <span>{t('account.googleLogin')}</span>}
        </button>
    );
}
